import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { GameView, ResolvedTrick } from '@crew/shared';
import { tableTransition } from './transitions.js';

export const REVEAL_MS = 450;
export const RECAP_MS = 1800;
export const ACTIVITY_MS = 2600;

/** Presentation never queues or gates the server's next turn. Remount per attempt. */
export function useTableActivity(game: GameView) {
  const previous = useRef(game);
  const sequence = useRef(0);
  const timers = useRef<number[]>([]);
  const [activity, setActivity] = useState<ReturnType<typeof tableTransition> & { id: number } | null>(null);
  const [recap, setRecap] = useState<{ trick: ResolvedTrick; phase: 'reveal' | 'settle' } | null>(null);
  const activityTimer = useRef<number>();

  useEffect(() => () => {
    timers.current.forEach(window.clearTimeout);
    window.clearTimeout(activityTimer.current);
  }, []);

  useLayoutEffect(() => {
    const change = tableTransition(previous.current, game);
    previous.current = game;
    if (change.reset) {
      timers.current.forEach(window.clearTimeout);
      window.clearTimeout(activityTimer.current);
      setActivity(null);
      setRecap(null);
      return;
    }
    if (change.plays.length || change.tasks.length) {
      setActivity({ ...change, id: ++sequence.current });
      window.clearTimeout(activityTimer.current);
      activityTimer.current = window.setTimeout(() => setActivity(null), ACTIVITY_MS);
    }
    if (change.completed) {
      timers.current.forEach(window.clearTimeout);
      const trick = change.completed;
      setRecap({ trick, phase: 'reveal' });
      timers.current = [
        window.setTimeout(() => setRecap({ trick, phase: 'settle' }), REVEAL_MS),
        window.setTimeout(() => setRecap(null), RECAP_MS)
      ];
    }
  }, [game]);
  return { activity, recap };
}